import { MIN_TERM_LENGTH } from "@/lib/searchHighlightTerms";
import type { VisibleTextSpan } from "./visibleTextRanges";

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Case-insensitive, and a space in the term matches any whitespace run,
 * non-breaking spaces included. Diacritics are not folded: global search
 * matches with `websearch_to_tsquery('simple')` and no `unaccent`, so "lov"
 * must not light "løv" (ADR 0001).
 */
const termPattern = (term: string): RegExp =>
  new RegExp(term.split(/\s+/).map(escapeRegExp).join("\\s+"), "giu");

/** Index of the first claimed span ending after `offset`. */
function firstEndingAfter(
  claimed: readonly VisibleTextSpan[],
  offset: number
): number {
  let lo = 0;
  let hi = claimed.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (claimed[mid].end <= offset) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

const overlapsClaimed = (
  claimed: readonly VisibleTextSpan[],
  span: VisibleTextSpan
): boolean => {
  const next = claimed[firstEndingAfter(claimed, span.start)];
  return next !== undefined && next.start < span.end;
};

/** Merges two sorted lists of non-overlapping spans. */
function mergeSpans(
  a: readonly VisibleTextSpan[],
  b: readonly VisibleTextSpan[]
): VisibleTextSpan[] {
  const merged: VisibleTextSpan[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (j >= b.length || (i < a.length && a[i].start < b[j].start)) {
      merged.push(a[i++]);
    } else {
      merged.push(b[j++]);
    }
  }
  return merged;
}

/**
 * The search hits for `terms` in `text` (a visible-text snapshot's
 * `fullText`), in document order. Matches cross element boundaries and partial
 * words match. Terms claim text in list order, so with phrases listed first a
 * word inside a matched phrase is not a second hit, and hits never overlap.
 */
export function findSearchHits(
  text: string,
  terms: readonly string[]
): VisibleTextSpan[] {
  let claimed: VisibleTextSpan[] = [];
  for (const term of terms) {
    if (term.trim().length < MIN_TERM_LENGTH) continue;
    const pattern = termPattern(term);
    const found: VisibleTextSpan[] = [];
    for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
      const span = { start: match.index, end: match.index + match[0].length };
      if (overlapsClaimed(claimed, span)) {
        // Try again from the next character, past the text already claimed.
        pattern.lastIndex = match.index + 1;
        continue;
      }
      found.push(span);
    }
    claimed = mergeSpans(claimed, found);
  }
  return claimed;
}
